import { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Building2, Pencil, Plus, Trash2 } from 'lucide-react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { getErrorMessage } from '@/lib/api'
import { orgTreeApi, type OrgTreeServiceArea, type OrgTreeServiceAreaPayload } from '@/services/org-tree'

type ServiceAreaFormState = {
  area: string
  postcode: string
  cityName: string
  district: string
  country: string
  state: string
}

const defaultFormState: ServiceAreaFormState = {
  area: '',
  postcode: '',
  cityName: '',
  district: '',
  country: '',
  state: '',
}

type NodeServiceAreaManagerProps = {
  nodeId: string | number
  serviceInstanceId?: string | null
}

export default function NodeServiceAreaManager({ nodeId, serviceInstanceId }: NodeServiceAreaManagerProps) {
  const queryClient = useQueryClient()
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [serviceAreaForm, setServiceAreaForm] = useState<ServiceAreaFormState>(defaultFormState)
  const [editingArea, setEditingArea] = useState<OrgTreeServiceArea | null>(null)

  const queryKey = ['org-tree-service-areas', nodeId, serviceInstanceId]

  const serviceAreasQuery = useQuery({
    queryKey,
    queryFn: () => orgTreeApi.listServiceAreas(nodeId, serviceInstanceId),
    enabled: Boolean(nodeId),
  })

  const countriesQuery = useQuery({
    queryKey: ['org-tree-countries'],
    queryFn: orgTreeApi.listCountries,
    staleTime: 60_000,
  })

  const statesQuery = useQuery({
    queryKey: ['org-tree-states', serviceAreaForm.country],
    queryFn: () => orgTreeApi.listStates(serviceAreaForm.country),
    enabled: Boolean(serviceAreaForm.country),
    staleTime: 60_000,
  })

  useEffect(() => {
    if (!editingArea || countriesQuery.data == null || serviceAreaForm.country) {
      return
    }

    const countryId = countriesQuery.data.find((country) => country.country_name === editingArea.country)?.id
    if (countryId != null) {
      setServiceAreaForm((current) => ({ ...current, country: String(countryId) }))
    }
  }, [countriesQuery.data, editingArea, serviceAreaForm.country])

  useEffect(() => {
    if (!editingArea || statesQuery.data == null || serviceAreaForm.state) {
      return
    }

    const stateId = statesQuery.data.find((state) => state.name === editingArea.state)?.id
    if (stateId != null) {
      setServiceAreaForm((current) => ({ ...current, state: String(stateId) }))
    }
  }, [editingArea, serviceAreaForm.state, statesQuery.data])

  const resetDialog = () => {
    setIsDialogOpen(false)
    setEditingArea(null)
    setServiceAreaForm(defaultFormState)
  }

  const invalidateServiceAreas = async () => {
    await queryClient.invalidateQueries({ queryKey })
  }

  const saveMutation = useMutation({
    mutationFn: async () => {
      if (!serviceAreaForm.postcode && !serviceAreaForm.cityName && !serviceAreaForm.country && !serviceAreaForm.state) {
        throw new Error('At least one location field is required')
      }

      const payload: OrgTreeServiceAreaPayload = {
        node: nodeId,
        area: serviceAreaForm.area || null,
        postcode: serviceAreaForm.postcode || null,
        city_name: serviceAreaForm.cityName || null,
        district: serviceAreaForm.district || null,
        country: serviceAreaForm.country || null,
        state: serviceAreaForm.state || null,
      }

      if (editingArea) {
        return orgTreeApi.updateServiceArea(editingArea.id, payload)
      }

      return orgTreeApi.createServiceArea(payload)
    },
    onSuccess: async () => {
      await invalidateServiceAreas()
      resetDialog()
      toast.success(editingArea ? 'Service area updated successfully' : 'Service area added successfully')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (serviceAreaId: string | number) => orgTreeApi.deleteServiceArea(serviceAreaId),
    onSuccess: async () => {
      await invalidateServiceAreas()
      toast.success('Service area deleted successfully')
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  const openCreate = () => {
    setEditingArea(null)
    setServiceAreaForm(defaultFormState)
    setIsDialogOpen(true)
  }

  const openEdit = (area: OrgTreeServiceArea) => {
    setEditingArea(area)
    setServiceAreaForm({
      area: area.area ?? '',
      postcode: area.postcode ?? '',
      cityName: area.city_name ?? '',
      district: area.district ?? '',
      country: '',
      state: '',
    })
    setIsDialogOpen(true)
  }

  const rows = useMemo(() => serviceAreasQuery.data ?? [], [serviceAreasQuery.data])
  const configuredCountries = Array.from(new Set(rows.map((row) => row.country).filter(Boolean))).length

  return (
    <>
      <Card className="overflow-hidden rounded-[24px] border-border/80 bg-gradient-to-b from-background to-muted/10 shadow-sm">
        <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <Building2 className="h-5 w-5" />
              </div>
              <div>
                <CardTitle className="text-xl">Service Area</CardTitle>
                <CardDescription>Active service-area rows attached to this node.</CardDescription>
              </div>
            </div>
            <div className="flex flex-wrap gap-2">
              <Badge variant="secondary">{rows.length} coverage zones</Badge>
              <Badge variant="outline">{configuredCountries} countries</Badge>
              <Badge variant="outline">Location scoped</Badge>
            </div>
          </div>
          <Button className="shadow-sm" onClick={openCreate}>
            <Plus className="mr-2 h-4 w-4" />
            Add Service Area
          </Button>
        </CardHeader>
        <CardContent className="space-y-5">
          {serviceAreasQuery.isLoading ? (
            <div className="rounded-3xl border border-dashed border-border/70 bg-muted/20 p-10 text-sm text-muted-foreground">
              Loading service areas...
            </div>
          ) : rows.length === 0 ? (
            <div className="rounded-3xl border border-dashed border-border/70 bg-linear-to-br from-primary/5 via-background to-muted/20 p-10 text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                <Building2 className="h-7 w-7" />
              </div>
              <p className="font-medium text-foreground">No service areas configured</p>
              <p className="mt-2 text-sm text-muted-foreground">Add the active coverage areas for this node to make delivery or support regions easier to manage.</p>
            </div>
          ) : (
            <>
              <div className="rounded-3xl border border-border/70 bg-background/90 p-5 shadow-sm">
                <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                  <div>
                    <p className="text-xs font-medium uppercase tracking-[0.18em] text-muted-foreground">Coverage Summary</p>
                    <p className="mt-2 text-base font-semibold text-foreground">Service areas are grouped by place, postcode, and state for faster review.</p>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {rows.slice(0, 3).map((row) => (
                      <Badge key={String(row.id)} variant="outline">{row.area || row.city_name || `Area ${row.id}`}</Badge>
                    ))}
                  </div>
                </div>
              </div>
              <div className="overflow-hidden rounded-3xl border border-border/70 bg-background shadow-sm">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/30 hover:bg-muted/30">
                    <TableHead className="pl-5">Area</TableHead>
                    <TableHead>Post Code</TableHead>
                    <TableHead>City</TableHead>
                    <TableHead>Country</TableHead>
                    <TableHead>State</TableHead>
                    <TableHead>District</TableHead>
                    <TableHead className="pr-5 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((area) => (
                    <TableRow key={String(area.id)}>
                      <TableCell className="pl-5">
                        <div className="flex flex-col gap-1">
                          <span className="font-medium text-foreground">{area.area || '-'}</span>
                          {area.city_name ? <span className="text-xs text-muted-foreground">{area.city_name}</span> : null}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">{area.postcode || '-'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{area.city_name || '-'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{area.country || '-'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{area.state || '-'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{area.district || '-'}</TableCell>
                      <TableCell className="pr-5">
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => openEdit(area)}>
                            <Pencil className="mr-1 h-4 w-4" />
                            Edit
                          </Button>
                          <Button variant="ghost" size="sm" className="text-destructive hover:bg-destructive/10 hover:text-destructive" onClick={() => deleteMutation.mutate(area.id)} disabled={deleteMutation.isPending}>
                            <Trash2 className="mr-1 h-4 w-4" />
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <Dialog open={isDialogOpen} onOpenChange={(open) => {
        if (!open) {
          resetDialog()
          return
        }

        setIsDialogOpen(true)
      }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingArea ? 'Edit Service Area' : 'Add Service Area'}</DialogTitle>
            <DialogDescription>Define the location coverage for this node.</DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-4">
            <div className="rounded-2xl border border-border/70 bg-muted/15 p-4">
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">Coverage Details</p>
                <Badge variant="secondary">At least one location field required</Badge>
              </div>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div className="grid gap-2">
              <Label htmlFor="service_area_area">Area</Label>
              <Input id="service_area_area" value={serviceAreaForm.area} onChange={(event) => setServiceAreaForm((current) => ({ ...current, area: event.target.value }))} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="service_area_postcode">Postcode</Label>
              <Input id="service_area_postcode" value={serviceAreaForm.postcode} onChange={(event) => setServiceAreaForm((current) => ({ ...current, postcode: event.target.value }))} />
            </div>
              </div>
            </div>

            <div className="rounded-2xl border border-border/70 bg-muted/15 p-4">
              <p className="text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">Location Mapping</p>
              <div className="mt-4 grid gap-4 sm:grid-cols-2">
                <div className="grid gap-2">
              <Label>Country</Label>
              <Select value={serviceAreaForm.country} onValueChange={(value) => setServiceAreaForm((current) => ({ ...current, country: value, state: '' }))}>
                <SelectTrigger aria-label="Service area country" className="w-full">
                  <SelectValue placeholder="Choose country" />
                </SelectTrigger>
                <SelectContent>
                  {(countriesQuery.data ?? []).map((country) => (
                    <SelectItem key={String(country.id)} value={String(country.id)}>
                      {country.country_name || `Country ${country.id}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
                <div className="grid gap-2">
              <Label>State</Label>
              <Select value={serviceAreaForm.state} onValueChange={(value) => setServiceAreaForm((current) => ({ ...current, state: value }))} disabled={!serviceAreaForm.country}>
                <SelectTrigger aria-label="Service area state" className="w-full">
                  <SelectValue placeholder={serviceAreaForm.country ? 'Choose state' : 'Choose country first'} />
                </SelectTrigger>
                <SelectContent>
                  {(statesQuery.data ?? []).map((state) => (
                    <SelectItem key={String(state.id)} value={String(state.id)}>
                      {state.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

                <div className="grid gap-2">
              <Label htmlFor="service_area_city">City</Label>
              <Input id="service_area_city" value={serviceAreaForm.cityName} onChange={(event) => setServiceAreaForm((current) => ({ ...current, cityName: event.target.value }))} />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="service_area_district">District</Label>
              <Input id="service_area_district" value={serviceAreaForm.district} onChange={(event) => setServiceAreaForm((current) => ({ ...current, district: event.target.value }))} />
            </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={resetDialog} disabled={saveMutation.isPending}>Cancel</Button>
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              {saveMutation.isPending ? 'Saving...' : editingArea ? 'Update Service Area' : 'Save Service Area'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}